import { useMemo } from "react";
import {
  Area,
  AreaChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useQuery } from "@tanstack/react-query";
import { listAccounts } from "../../lib/api-modules/accounts.js";
import type { AccountSummary, HousingUnit } from "../../lib/api-modules/types.js";
import { amortize, monthlyPayment } from "../../lib/util/mortgage.js";
import { formatCentsCurrency as formatCents } from "../../lib/util/money.js";

/**
 * Per-unit Amortization tab. Resolves linked mortgage / second_mortgage /
 * heloc accounts off the `HousingUnit.linked_account_ids` array; runs
 * `amortize()` per-loan; renders a stacked AreaChart of principal vs.
 * interest over months.
 *
 * **HELOC special-case**: HELOCs typically don't fully amortize the way
 * a fixed mortgage does (variable rate, drawing-credit-line behavior).
 * For HELOCs, surface a balance / available / minimum-payment tile
 * instead of an amortization curve.
 */

interface AmortizationTabProps {
  readonly unit: HousingUnit;
}

export function AmortizationTab({ unit }: AmortizationTabProps) {
  const accountsQuery = useQuery({
    queryKey: ["accounts"],
    queryFn: () => listAccounts(),
  });

  const linkedAccounts = useMemo<AccountSummary[]>(() => {
    const ids = new Set(unit.linked_account_ids ?? []);
    return (accountsQuery.data?.accounts ?? []).filter((a) =>
      ids.has(a.account_id),
    );
  }, [accountsQuery.data, unit.linked_account_ids]);

  if (accountsQuery.isLoading) {
    return <p className="text-sm text-zinc-500">Loading linked accounts…</p>;
  }

  if (linkedAccounts.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-zinc-300 p-6 text-center text-sm text-zinc-500 dark:border-zinc-700">
        No mortgage / HELOC / HOA accounts linked to this unit. Edit the
        unit and check the linked-accounts box for any{" "}
        <code className="rounded bg-zinc-100 px-1 py-0.5 dark:bg-zinc-800">
          mortgage
        </code>
        ,{" "}
        <code className="rounded bg-zinc-100 px-1 py-0.5 dark:bg-zinc-800">
          second_mortgage
        </code>
        , or{" "}
        <code className="rounded bg-zinc-100 px-1 py-0.5 dark:bg-zinc-800">
          heloc
        </code>{" "}
        accounts you've already added.
      </div>
    );
  }

  const mortgages = linkedAccounts.filter(
    (a) => a.account_type === "mortgage" || a.account_type === "second_mortgage",
  );
  const helocs = linkedAccounts.filter((a) => a.account_type === "heloc");

  return (
    <div className="flex flex-col gap-6">
      {mortgages.map((m) => (
        <MortgageCard key={m.account_id} account={m} />
      ))}
      {helocs.length > 0 && (
        <section>
          <h3 className="mb-2 text-sm font-medium">HELOC lines</h3>
          <ul className="flex flex-col gap-2">
            {helocs.map((h) => (
              <li
                key={h.account_id}
                className="rounded-md border border-zinc-200 p-3 text-sm dark:border-zinc-800"
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium">{h.name}</span>
                  <span className="text-xs text-zinc-500">{h.institution}</span>
                </div>
                <div className="mt-2 grid grid-cols-1 gap-2 text-xs sm:grid-cols-3">
                  <div>
                    <div className="text-zinc-500">Current balance</div>
                    <div className="font-mono">
                      {h.current_principal_cents !== undefined
                        ? formatCents(-h.current_principal_cents)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Minimum payment</div>
                    <div className="font-mono">
                      {h.minimum_payment_cents !== undefined
                        ? formatCents(h.minimum_payment_cents)
                        : "—"}
                    </div>
                  </div>
                  <div>
                    <div className="text-zinc-500">Rate</div>
                    <div className="font-mono">
                      {h.interest_rate_pct !== undefined
                        ? `${h.interest_rate_pct.toFixed(3)}%`
                        : "—"}
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-xs text-zinc-500">
                  HELOCs have variable rates + draw / repay flexibility, so
                  a fixed amortization schedule isn't applicable. Track
                  payments via the Expenses tab with{" "}
                  <code>expense_type: heloc_payment</code>.
                </p>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

function MortgageCard({ account }: { account: AccountSummary }) {
  const principal = account.current_principal_cents;
  const rate = account.interest_rate_pct;
  const explicitPayment = account.minimum_payment_cents;

  // Months remaining: derive from payoff_date if present; otherwise
  // assume 360 months from today (best guess for new mortgages).
  const monthsRemaining = useMemo(() => {
    if (account.payoff_date) {
      const today = new Date();
      const payoff = new Date(account.payoff_date);
      const monthsDiff =
        (payoff.getUTCFullYear() - today.getUTCFullYear()) * 12 +
        (payoff.getUTCMonth() - today.getUTCMonth());
      return Math.max(1, monthsDiff);
    }
    return 360;
  }, [account.payoff_date]);

  if (principal === undefined || rate === undefined) {
    return (
      <section className="rounded-md border border-zinc-200 p-3 text-sm dark:border-zinc-800">
        <header className="font-medium">{account.name}</header>
        <p className="mt-2 text-xs text-zinc-500">
          Need <code>current_principal_cents</code> and{" "}
          <code>interest_rate_pct</code> on the account row to render
          amortization. Edit the account to fill them in.
        </p>
      </section>
    );
  }

  const monthlyPmt = explicitPayment ?? monthlyPayment(principal, rate, monthsRemaining);
  const result = amortize({
    principal_cents: principal,
    rate_pct_yearly: rate,
    months_remaining: monthsRemaining,
    monthly_payment_cents: monthlyPmt,
  });

  // Reduce to one row per year for chart legibility (a 360-row chart
  // plots fine but the trend is clearer at year-granularity).
  const chartData = result.schedule
    .filter((r) => r.month % 12 === 0 || r.month === result.schedule.length)
    .map((r) => ({
      year: Math.ceil(r.month / 12),
      principal_dollars: r.principal_paid_cents / 100,
      interest_dollars: r.interest_paid_cents / 100,
      remaining_dollars: r.remaining_balance_cents / 100,
    }));

  return (
    <section className="rounded-md border border-zinc-200 p-4 dark:border-zinc-800">
      <header className="mb-3 flex items-baseline justify-between">
        <div>
          <h3 className="text-base font-medium">{account.name}</h3>
          <p className="text-xs text-zinc-500">
            {account.account_type} · {account.institution ?? "—"}
          </p>
        </div>
        <div className="text-right text-xs">
          <div>
            <span className="text-zinc-500">Monthly payment: </span>
            <span className="font-mono">{formatCents(monthlyPmt)}</span>
          </div>
          <div>
            <span className="text-zinc-500">Lifetime interest: </span>
            <span className="font-mono">
              {formatCents(result.total_interest_cents)}
            </span>
          </div>
        </div>
      </header>
      <div className="h-64 w-full">
        <ResponsiveContainer width="100%" height="100%" minWidth={0}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeOpacity={0.15} vertical={false} />
            <XAxis
              dataKey="year"
              tick={{ fontSize: 11 }}
              label={{ value: "Year", position: "insideBottom", offset: -2 }}
            />
            <YAxis tick={{ fontSize: 11 }} width={64} />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="remaining_dollars"
              stackId="1"
              stroke="#6366f1"
              fill="#6366f1"
              fillOpacity={0.3}
              name="Balance"
            />
            <Area
              type="monotone"
              dataKey="interest_dollars"
              stackId="2"
              stroke="#ef4444"
              fill="#ef4444"
              fillOpacity={0.3}
              name="Interest paid (year)"
            />
            <Area
              type="monotone"
              dataKey="principal_dollars"
              stackId="2"
              stroke="#10b981"
              fill="#10b981"
              fillOpacity={0.3}
              name="Principal paid (year)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
}
